import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { NotionIcon } from "./notionIcon";
import { type NotionPagePayload, parseNotionPagePayload } from "./notionPageSchema";

interface NotionPageRendererProps {
  block: StreamingBlock;
}

const safeNotionUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

export const NotionPageRenderer = (props: NotionPageRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<NotionPagePayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseNotionPagePayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("notion_page_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
              <NotionIcon class="size-4 shrink-0" />
              <Show
                when={safeNotionUrl(data().url)}
                fallback={
                  <span class="truncate text-sm font-medium text-primary-text">
                    {data().title || rt.t("notion_page_card.title")}
                  </span>
                }
              >
                {(href) => (
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-brand hover:underline"
                  >
                    <span class="truncate">{data().title || rt.t("notion_page_card.title")}</span>
                    <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                  </a>
                )}
              </Show>
            </div>

            <Show
              when={data().markdown.trim()}
              fallback={<div class="p-3 text-sm text-muted-text">{rt.t("notion_page_card.empty")}</div>}
            >
              <div class="px-3 py-2">
                <ScrollableFadeContainer maxHeight="200px">
                  <CompleteMarkdown markdown={data().markdown} />
                </ScrollableFadeContainer>
              </div>
            </Show>

            <Show when={data().truncated}>
              <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                {rt.t("notion_page_card.truncated")}
              </div>
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
